import { DomainError, invariant } from '../shared/errors';
import type { Vnd } from '../shared/money';
import type { IsoDateTime } from '../shared/types';

export const VEHICLE_TYPES = ['Xe máy', 'Xe điện', 'Xe đạp'] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

export const SHIFTS = ['morning', 'afternoon', 'evening'] as const;
export type ShipperShift = (typeof SHIFTS)[number];

export const SHIFT_LABELS: Record<ShipperShift, { label: string; time: string }> = {
  morning: { label: 'Sáng', time: '7–11h' },
  afternoon: { label: 'Chiều', time: '11–17h' },
  evening: { label: 'Tối', time: '17–22h' },
};

export const SHIPPER_DOCUMENTS = ['idFront', 'idBack', 'license', 'portrait', 'bankAccount'] as const;
export type ShipperDocument = (typeof SHIPPER_DOCUMENTS)[number];

export const SHIPPER_DOCUMENT_LABELS: Record<ShipperDocument, string> = {
  idFront: 'CCCD mặt trước',
  idBack: 'CCCD mặt sau',
  license: 'Bằng lái xe',
  portrait: 'Ảnh chân dung',
  bankAccount: 'TK ngân hàng',
};

export type ShipperStatus = 'pending' | 'active' | 'rejected' | 'suspended';

export type ShipperProps = {
  id: string;
  userId: string;
  name: string;
  phone: string;
  vehicleType: VehicleType;
  vehicleModel: string;
  plate: string;
  areas: string[];
  shifts: ShipperShift[];
  documents: Partial<Record<ShipperDocument, string>>;
  bankAccount: string | null;
  status: ShipperStatus;
  online: boolean;
  codHolding: Vnd;
  rating: number;
  ratingCount: number;
  deliveredCount: number;
  onTimeCount: number;
  appliedAt: IsoDateTime;
  reviewedAt: IsoDateTime | null;
  rejectReason: string | null;
};

export class Shipper {
  private constructor(private props: ShipperProps) {}

  static apply(
    input: Pick<ShipperProps, 'id' | 'userId' | 'name' | 'phone' | 'vehicleType' | 'vehicleModel' | 'plate' | 'areas' | 'shifts' | 'documents' | 'bankAccount' | 'appliedAt'>,
  ): Shipper {
    invariant(input.name.trim().length > 1, 'Cần họ tên');
    invariant(input.plate.trim().length >= 5 || input.vehicleType === 'Xe đạp', 'Cần biển số xe');
    invariant(input.areas.length > 0, 'Chọn ít nhất một khu vực nhận đơn');
    invariant(input.shifts.length > 0, 'Chọn ít nhất một ca có thể chạy');
    return new Shipper({
      ...input,
      status: 'pending',
      online: false,
      codHolding: 0,
      rating: 5,
      ratingCount: 0,
      deliveredCount: 0,
      onTimeCount: 0,
      reviewedAt: null,
      rejectReason: null,
    });
  }

  static restore(props: ShipperProps): Shipper {
    return new Shipper(props);
  }

  get id(): string {
    return this.props.id;
  }
  get status(): ShipperStatus {
    return this.props.status;
  }
  get online(): boolean {
    return this.props.online;
  }

  snapshot(): ShipperProps {
    return structuredClone(this.props);
  }

  missingDocuments(): ShipperDocument[] {
    return SHIPPER_DOCUMENTS.filter((doc) => !this.props.documents[doc]);
  }

  attachDocument(doc: ShipperDocument, url: string): void {
    this.props.documents = { ...this.props.documents, [doc]: url };
  }

  approve(at: IsoDateTime): void {
    invariant(this.props.status === 'pending', 'Hồ sơ không ở trạng thái chờ duyệt', 'INVALID_STATE');
    const missing = this.missingDocuments();
    if (missing.length > 0) {
      throw new DomainError('INVALID_STATE', `Hồ sơ còn thiếu: ${missing.map((d) => SHIPPER_DOCUMENT_LABELS[d]).join(', ')}`);
    }
    this.props.status = 'active';
    this.props.reviewedAt = at;
  }

  reject(reason: string, at: IsoDateTime): void {
    invariant(this.props.status === 'pending', 'Hồ sơ không ở trạng thái chờ duyệt', 'INVALID_STATE');
    this.props.status = 'rejected';
    this.props.rejectReason = reason.trim() || null;
    this.props.reviewedAt = at;
  }

  suspend(): void {
    invariant(this.props.status === 'active', 'Chỉ tạm khóa shipper đang hợp tác', 'INVALID_STATE');
    this.props.status = 'suspended';
    this.props.online = false;
  }

  reinstate(): void {
    invariant(this.props.status === 'suspended', 'Shipper không bị khóa', 'INVALID_STATE');
    this.props.status = 'active';
  }

  goOnline(online: boolean): void {
    if (online) invariant(this.props.status === 'active', 'Tài khoản shipper chưa được duyệt', 'FORBIDDEN');
    this.props.online = online;
  }

  assertCanTakeOrders(): void {
    if (this.props.status !== 'active') throw new DomainError('FORBIDDEN', 'Tài khoản shipper chưa được duyệt');
    if (!this.props.online) throw new DomainError('INVALID_STATE', 'Bật "Đang nhận đơn" để nhận đơn');
  }

  recordDelivery(codCollected: Vnd, onTime: boolean): void {
    this.props.deliveredCount += 1;
    if (onTime) this.props.onTimeCount += 1;
    this.props.codHolding += codCollected;
  }

  /** Shipper hands over COD cash to the store. */
  settleCod(amount: Vnd): void {
    invariant(amount > 0 && amount <= this.props.codHolding, 'Số tiền đối soát không hợp lệ');
    this.props.codHolding -= amount;
  }

  rate(stars: number): void {
    invariant(stars >= 1 && stars <= 5, 'Đánh giá từ 1 đến 5 sao');
    const total = this.props.rating * this.props.ratingCount + stars;
    this.props.ratingCount += 1;
    this.props.rating = Math.round((total / this.props.ratingCount) * 10) / 10;
  }
}
